import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';
import { formatPrice } from './index';
import { translate } from '../i18n/translateUtils';
import { calculateCustomerGrandTotal } from './customerFinancialUtils';
import { TUL_PILE_MODELS, PRODUCT_TYPES, STOCK_METER_PRODUCT_TYPES, COMPANY_CONTACT, LABEL_SIZE_MM } from '../constants';
import { ROBOTO_REGULAR_BASE64, ROBOTO_BOLD_BASE64 } from '../assets/fonts/robotoFont';
import { MEDA_FABRIC_LOGO_DATA_URL, MEDA_FABRIC_LOGO_ASPECT } from '../assets/medaFabricLogo';

// Ana Renk - Bronz/Kahve
const PRIMARY_COLOR = [176, 67, 9]; // #b04309
const SECONDARY_COLOR = [143, 53, 14]; // #8f350e
const LIGHT_BG = [254, 247, 237]; // #fef7ed
const GRAY_TEXT = [100, 100, 100];

// PDF'ler her zaman Rusça oluşturulur. Kiril + Latin destekleyen Roboto fontu kullanılır.
const PDF_FONT = 'Roboto';
const PDF_LANG = { lng: 'ru' };

// Roboto fontunu (Kiril + Latin + Türkçe) jsPDF dokümanına kaydet
const registerRobotoFont = (doc) => {
  doc.addFileToVFS('Roboto-Regular.ttf', ROBOTO_REGULAR_BASE64);
  doc.addFont('Roboto-Regular.ttf', PDF_FONT, 'normal');
  doc.addFileToVFS('Roboto-Bold.ttf', ROBOTO_BOLD_BASE64);
  doc.addFont('Roboto-Bold.ttf', PDF_FONT, 'bold');
  doc.setFont(PDF_FONT, 'normal');
};

// Unicode font kullanıldığı için karakter temizlemeye gerek yok (Kiril/Türkçe korunur)
const cleanTurkishChars = (text) => (text || '');

// Ürün tipi etiketi (her zaman Rusça)
const getProductTypeLabel = (type) => translate(`constants.productTypes.${type}`, {
  ...PDF_LANG,
  defaultValue: PRODUCT_TYPES[type]?.label || type
});

// Pile modeli etiketi (DB'de Türkçe saklanır → Rusça'ya çevrilir)
const getPdfPileModel = (raw) => {
  if (!raw) return '';
  const entry = Object.entries(TUL_PILE_MODELS).find(([, val]) => val === raw);
  return entry ? translate(`constants.pileModels.${entry[0]}`, { ...PDF_LANG, defaultValue: raw }) : raw;
};

// Logo ekle (mm cinsinden yükseklik)
const addLogoToDoc = (doc, x, y, maxHeight) => {
  const height = maxHeight;
  const width = height * MEDA_FABRIC_LOGO_ASPECT;
  doc.addImage(MEDA_FABRIC_LOGO_DATA_URL, 'PNG', x, y, width, height);
  return { width, height };
};

const openPdfInNewTab = (doc) => {
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

const createLabelPdfDoc = () => new jsPDF({
  unit: 'mm',
  orientation: 'landscape',
  format: [LABEL_SIZE_MM.height, LABEL_SIZE_MM.width]
});

let labelQrCache = null;
let retailPdfQrCache = null;
let dealerPdfQrCache = null;

const getLabelQrCodes = async () => {
  const cacheKey = `${COMPANY_CONTACT.dealerWhatsappUrl}|${COMPANY_CONTACT.instagramUrl}`;
  if (labelQrCache?.cacheKey === cacheKey) return labelQrCache;

  const [whatsapp, instagram] = await Promise.all([
    QRCode.toDataURL(COMPANY_CONTACT.dealerWhatsappUrl, { margin: 0, errorCorrectionLevel: 'M' }),
    QRCode.toDataURL(COMPANY_CONTACT.instagramUrl, { margin: 0, errorCorrectionLevel: 'M' })
  ]);

  labelQrCache = { cacheKey, whatsapp, instagram };
  return labelQrCache;
};

const getRetailPdfQrCodes = async () => {
  const cacheKey = `${COMPANY_CONTACT.whatsappUrl}|${COMPANY_CONTACT.retailInstagramUrl}`;
  if (retailPdfQrCache?.cacheKey === cacheKey) return retailPdfQrCache;

  const [whatsapp, instagram] = await Promise.all([
    QRCode.toDataURL(COMPANY_CONTACT.whatsappUrl, { margin: 0, errorCorrectionLevel: 'M' }),
    QRCode.toDataURL(COMPANY_CONTACT.retailInstagramUrl, { margin: 0, errorCorrectionLevel: 'M' })
  ]);

  retailPdfQrCache = { cacheKey, whatsapp, instagram };
  return retailPdfQrCache;
};

const getDealerPdfQrCodes = async () => {
  const cacheKey = `${COMPANY_CONTACT.dealerWhatsappUrl}|${COMPANY_CONTACT.instagramUrl}`;
  if (dealerPdfQrCache?.cacheKey === cacheKey) return dealerPdfQrCache;

  const [whatsapp, instagram] = await Promise.all([
    QRCode.toDataURL(COMPANY_CONTACT.dealerWhatsappUrl, { margin: 0, errorCorrectionLevel: 'M' }),
    QRCode.toDataURL(COMPANY_CONTACT.instagramUrl, { margin: 0, errorCorrectionLevel: 'M' })
  ]);

  dealerPdfQrCache = { cacheKey, whatsapp, instagram };
  return dealerPdfQrCache;
};

const drawHeaderQrCodes = (doc, qrCodes) => {
  const pageW = doc.internal.pageSize.getWidth();
  const qrSize = 11;
  const qrGap = 2;
  const marginR = 6;
  const instagramX = pageW - marginR - qrSize;
  const whatsappX = instagramX - qrGap - qrSize;
  const qrY = 4;

  doc.addImage(qrCodes.whatsapp, 'PNG', whatsappX, qrY, qrSize, qrSize);
  doc.addImage(qrCodes.instagram, 'PNG', instagramX, qrY, qrSize, qrSize);

  return {
    whatsappX,
    instagramX,
    qrSize,
    qrY,
    qrBlockCenterX: (whatsappX + instagramX + qrSize) / 2,
    qrBottomY: qrY + qrSize
  };
};

const drawHeaderContactBelowQr = (doc, qrLayout, phone) => {
  let y = qrLayout.qrBottomY + 4.5;
  doc.setFontSize(7);
  doc.text(`Тел.: ${phone}`, qrLayout.qrBlockCenterX, y, { align: 'center' });
  y += 3.2;
  doc.text(COMPANY_CONTACT.email, qrLayout.qrBlockCenterX, y, { align: 'center' });
  y += 3.2;
  const addressLines = doc.splitTextToSize(COMPANY_CONTACT.address, 36);
  addressLines.slice(0, 2).forEach((line) => {
    doc.text(line, qrLayout.qrBlockCenterX, y, { align: 'center' });
    y += 3.2;
  });
};

const formatPdfDateLabel = (value) => {
  if (!value) return '';
  const normalized = String(value).includes('T') ? value : `${value}T00:00:00`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('ru-RU');
};

const getDealerLabelProductName = (order) => {
  if (!order) return '-';

  let productName = '';
  if (order.productType === 'plise') {
    productName = [order.pliseProduct1Name, order.pliseProduct2Name].filter(Boolean).join(' + ');
  } else {
    productName = order.productName || '';
    if (order.productVariant) productName += ` - ${order.productVariant}`;
  }
  if (!productName) {
    productName = getProductTypeLabel(order.productType);
  }

  return cleanTurkishChars(productName);
};

const getDealerLabelMetersText = (order) => {
  if (!order) return '';

  if (STOCK_METER_PRODUCT_TYPES.includes(order.productType)) {
    const widthM = parseFloat(order.width) || 0;
    const qty = parseFloat(order.quantity) || 1;
    if (widthM <= 0) return '';
    const totalM = widthM * qty;
    return qty > 1 ? `${totalM} m` : `${widthM} m`;
  }

  if (order.productType === 'raySistemi' && order.usedMeter) {
    return `${order.usedMeter} m`;
  }

  if (order.usedMeter) {
    return `${order.usedMeter} m`;
  }

  if (order.width && order.height) {
    return `${order.width} x ${order.height}`;
  }

  if (order.width) {
    return `${order.width}`;
  }

  return '';
};

const getDealerLabelProductInfo = (order) => {
  if (!order) return '-';

  const productName = getDealerLabelProductName(order);
  const meters = getDealerLabelMetersText(order);
  return meters ? `${productName} — ${meters}` : productName;
};

export const buildDealerLabelInfo = (source = {}, session = null, order = null) => {
  const sessionDate = session?.orderDate || session?.sessionDate;
  const dateSuffix = sessionDate ? ` (${formatPdfDateLabel(sessionDate)})` : '';
  const dealerName = source.companyName || source.name || session?.dealerName || '-';

  const customer = order
    ? dealerName
    : `${dealerName}${dateSuffix}`.trim();

  return {
    customer: customer.trim(),
    productInfo: getDealerLabelProductInfo(order),
    address: source.address || '-',
    phone: source.phone1 || source.phone || '-'
  };
};

const drawDealerLabelPage = async (doc, labelInfo) => {
  registerRobotoFont(doc);

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 4;
  const contentWidth = pageW - (margin * 2);

  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.25);
  doc.rect(2, 2, pageW - 4, pageH - 4);

  const logoHeight = 14;
  addLogoToDoc(doc, margin, margin, logoHeight);

  const qrCodes = await getLabelQrCodes();
  const qrSize = 10;
  const qrGap = 2;
  const qrY = margin + 1;
  const instagramX = pageW - margin - qrSize;
  const whatsappX = instagramX - qrGap - qrSize;

  doc.addImage(qrCodes.whatsapp, 'PNG', whatsappX, qrY, qrSize, qrSize);
  doc.addImage(qrCodes.instagram, 'PNG', instagramX, qrY, qrSize, qrSize);

  const topRowBottom = Math.max(margin + logoHeight, qrY + qrSize);
  const fieldsStartY = topRowBottom + 6;
  const fields = [
    { label: 'Клиент:', value: cleanTurkishChars(labelInfo.customer || '-'), fontSize: 11 },
    { label: 'Товар:', value: cleanTurkishChars(labelInfo.productInfo || '-'), fontSize: 11 },
    { label: 'Адрес:', value: cleanTurkishChars(labelInfo.address || '-'), fontSize: 11 },
    { label: 'Тел.:', value: labelInfo.phone || '-', fontSize: 13 }
  ];

  doc.setTextColor(0, 0, 0);

  let currentY = fieldsStartY;
  const labelColWidth = 18;
  const valueWidth = contentWidth - labelColWidth;

  fields.forEach(({ label, value, fontSize }) => {
    const lineHeight = fontSize * 0.5;

    doc.setFontSize(fontSize);
    doc.setFont(PDF_FONT, 'bold');
    doc.text(label, margin, currentY);

    doc.setFont(PDF_FONT, 'normal');
    const lines = doc.splitTextToSize(String(value), valueWidth);
    lines.forEach((line, index) => {
      doc.text(line, margin + labelColWidth, currentY + (index * lineHeight));
    });

    currentY += Math.max(fontSize + 1, lines.length * lineHeight + 2.5);
  });

  doc.setFont(PDF_FONT, 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('Спасибо за покупку!', pageW / 2, pageH - margin, { align: 'center' });
};

// Modern Header çiz
const drawHeader = (doc, title, subTitle = '', headerOptions = {}) => {
  const { qrCodes = null, contactPhone = COMPANY_CONTACT.phone } = headerOptions;
  const logoX = 8;
  const logoY = 4;
  const logoH = 14;
  const logoBottomGap = 4.5;
  const headerHeight = 38;

  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(0, 0, 210, headerHeight, 'F');

  addLogoToDoc(doc, logoX, logoY, logoH);

  doc.setTextColor(255, 255, 255);

  const textX = logoX;
  let textY = logoY + logoH + logoBottomGap;
  const titleMaxWidth = qrCodes ? 118 : 130;

  if (subTitle) {
    doc.setFontSize(11);
    doc.setFont(PDF_FONT, 'bold');
    const subTitleLines = doc.splitTextToSize(cleanTurkishChars(subTitle).toUpperCase(), titleMaxWidth);
    doc.text(subTitleLines, textX, textY);
    textY += subTitleLines.length * 4.5 + 1;
    doc.setFontSize(9);
    doc.setFont(PDF_FONT, 'normal');
    const titleLines = doc.splitTextToSize(cleanTurkishChars(title), titleMaxWidth);
    doc.text(titleLines, textX, textY);
  } else {
    doc.setFontSize(10);
    doc.setFont(PDF_FONT, 'normal');
    const titleLines = doc.splitTextToSize(cleanTurkishChars(title), titleMaxWidth);
    doc.text(titleLines, textX, textY);
  }

  if (qrCodes) {
    const qrLayout = drawHeaderQrCodes(doc, qrCodes);
    drawHeaderContactBelowQr(doc, qrLayout, contactPhone);
  } else {
    doc.setFontSize(9);
    doc.text(`Тел.: ${COMPANY_CONTACT.phone}`, 190, 14, { align: 'right' });
    doc.text(COMPANY_CONTACT.email, 190, 22, { align: 'right' });
    doc.text(COMPANY_CONTACT.address, 190, 30, { align: 'right' });
  }

  doc.setTextColor(0, 0, 0);
};

// Modern Footer çiz
const drawFooter = (doc) => {
  const pageHeight = doc.internal.pageSize.height;

  // Alt çizgi
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.5);
  doc.line(20, pageHeight - 25, 190, pageHeight - 25);

  // Footer text
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_TEXT);
  doc.text(translate('pdf.footerTagline', { ...PDF_LANG, defaultValue: 'MEDA FABRIC - Качественные шторные решения' }), 105, pageHeight - 18, { align: 'center' });
  doc.text(translate('pdf.footerNote', { ...PDF_LANG, defaultValue: 'Этот документ создан в электронном виде.' }), 105, pageHeight - 12, { align: 'center' });

  // Sayfa numarası
  const pageCount = doc.internal.getNumberOfPages();
  doc.text(translate('pdf.page', { ...PDF_LANG, page: doc.internal.getCurrentPageInfo().pageNumber, total: pageCount, defaultValue: `Страница ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}` }), 190, pageHeight - 12, { align: 'right' });

  doc.setTextColor(0, 0, 0);
};

// Metni satırlara böl
const splitTextToLines = (text, maxCharsPerLine) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach(word => {
    if ((currentLine + ' ' + word).trim().length <= maxCharsPerLine) {
      currentLine = (currentLine + ' ' + word).trim();
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);
  return lines;
};

// Müşteri bilgileri kutusu
const drawCustomerInfoBox = (doc, customerInfo, startY) => {
  const cleanName = cleanTurkishChars(customerInfo.name || '');
  const cleanAddress = cleanTurkishChars(customerInfo.address || '');

  // Adresi satırlara böl (maks 60 karakter per satır)
  const addressLines = splitTextToLines(cleanAddress, 60);
  const extraLines = Math.max(0, addressLines.length - 1);
  const boxHeight = 45 + (extraLines * 7);

  // Kutu arka planı
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(15, startY, 180, boxHeight, 3, 3, 'F');

  // Sol taraf - Müşteri bilgileri
  doc.setFontSize(11);
  doc.setFont(PDF_FONT, 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('ИНФОРМАЦИЯ О КЛИЕНТЕ', 20, startY + 10);

  doc.setFontSize(10);
  doc.setFont(PDF_FONT, 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(`ФИО: ${cleanName}`, 20, startY + 20);
  doc.text(`Телефон: ${customerInfo.phone1 || '-'}`, 20, startY + 28);

  // Adres satırları
  let addressY = startY + 36;
  addressLines.forEach((line, index) => {
    const prefix = index === 0 ? 'Адрес: ' : '       ';
    doc.text(`${prefix}${line}`, 20, addressY);
    addressY += 7;
  });

  // Sağ taraf - Tarihler
  if (customerInfo.orderDate || customerInfo.deliveryDate) {
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_TEXT);

    if (customerInfo.orderDate) {
      const orderDate = customerInfo.orderDate?.toDate ? customerInfo.orderDate.toDate() : new Date(customerInfo.orderDate);
      doc.text(`Заказ: ${orderDate.toLocaleDateString('ru-RU')}`, 190, startY + 20, { align: 'right' });
    }

    if (customerInfo.deliveryDate) {
      const deliveryDate = customerInfo.deliveryDate?.toDate ? customerInfo.deliveryDate.toDate() : new Date(customerInfo.deliveryDate);
      doc.text(`Доставка: ${deliveryDate.toLocaleDateString('ru-RU')}`, 190, startY + 28, { align: 'right' });
    }
  }

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 8;
};

// Toplam tutar kutusu - Sağda alt alta format
const drawTotalBox = (doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, startY, showPaymentInfo = false, paidAmount = 0, remainingAmount = 0, showDetailedPricing = true, customerInfo = {}, orders = []) => {
  const gIskonto = parseFloat(genelIskontoYuzde) || 0;

  const hasManualOverride = customerInfo?.manualTotalOverride != null && customerInfo?.manualTotalOverride !== '';
  const hasIskonto = gIskonto > 0 && !hasManualOverride;
  const hasProductDiscount = discountedTotalAmount !== totalAmount && discountedTotalAmount > 0;

  const iskontoluToplam = totalAmount - (totalAmount * gIskonto / 100);

  const grandTotal = calculateCustomerGrandTotal(
    { ...customerInfo, genelIskontoYuzde },
    orders
  );

  // Kutu yüksekliğini hesapla
  let itemCount = 1; // Sadece Genel toplam (alt sınır)
  if (showDetailedPricing) {
    itemCount += 1; // Ara toplam
    if (hasProductDiscount) itemCount++;
    if (hasIskonto) itemCount += 2; // % + tutar
  }

  if (showPaymentInfo) {
    itemCount += 2; // Ödenen + Kalan
  }
  const boxHeight = 20 + (itemCount * 10);

  // Ana kutu
  doc.setFillColor(...PRIMARY_COLOR);
  doc.roundedRect(100, startY, 95, boxHeight, 3, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont(PDF_FONT, 'normal');

  let lineY = startY + 12;

  if (showDetailedPricing) {
    // Ara Toplam (Brüt)
    doc.text('Промежуточный итог:', 105, lineY);
    doc.text(`${formatPrice(totalAmount)} ₸`, 190, lineY, { align: 'right' });
    lineY += 9;

    // Ürün İndirimli Toplam
    if (hasProductDiscount) {
      doc.text('Итог со скидкой на товар:', 105, lineY);
      doc.text(`${formatPrice(discountedTotalAmount)} ₸`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Genel İskonto
    if (hasIskonto) {
      doc.text(`Общая скидка (${genelIskontoYuzde}%):`, 105, lineY);
      doc.text(`-${formatPrice(totalAmount * gIskonto / 100)} ₸`, 190, lineY, { align: 'right' });
      lineY += 9;
      
      doc.text('Итог со скидкой:', 105, lineY);
      doc.text(`${formatPrice(iskontoluToplam)} ₸`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Ayırıcı çizgi (sadece detaylı görünümde ara toplamlar varsa)
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);
  }


  // Genel toplam
  doc.setFont(PDF_FONT, 'bold');
  doc.setFontSize(11);
  doc.text('ИТОГО (нетто):', 105, lineY + 6);
  doc.text(`${formatPrice(grandTotal)} ₸`, 190, lineY + 6, { align: 'right' });
  lineY += 14;

  // Ödenen ve Kalan bilgileri (sadece Müşteri PDF için)
  if (showPaymentInfo) {
    // Ayırıcı çizgi
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);

    doc.setFont(PDF_FONT, 'normal');
    doc.setFontSize(9);

    // Ödenen
    doc.text('Оплачено:', 105, lineY + 4);
    doc.text(`${formatPrice(paidAmount)} ₸`, 190, lineY + 4, { align: 'right' });
    lineY += 9;

    // Kalan
    const isPaymentComplete = Math.abs(remainingAmount) < 0.01 || remainingAmount <= 0;
    doc.setFont(PDF_FONT, 'bold');
    doc.text('Остаток:', 105, lineY + 4);
    if (isPaymentComplete) {
      doc.text('ОПЛАЧЕНО ПОЛНОСТЬЮ', 190, lineY + 4, { align: 'right' });
    } else {
      doc.text(`${formatPrice(remainingAmount)} ₸`, 190, lineY + 4, { align: 'right' });
    }
  }

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 10;
};

// Müşteri PDF tablo verisini oluşturur
const buildCustomerTableData = (orders, includeMeasurements = true) => {
  const head = includeMeasurements
    ? [['Тип товара', 'Комната', 'Название', 'Размеры(см)', 'Модель складки', 'Кол-во', 'Сумма (₸)', 'Со скидкой (₸)']]
    : [['Тип товара', 'Комната', 'Название', 'Модель складки', 'Кол-во', 'Сумма (₸)', 'Со скидкой (₸)']];

  const body = orders.flatMap((order) => {
    const cleanProductType = getProductTypeLabel(order.productType);
    const cleanRoomName = order.roomName || '';
    const cleanPileModel = getPdfPileModel(order.pileModel);
    const discountRate = parseFloat(order.discount) || 0;

    // Plise için özel satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      return order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Мультисистема)' : '';
        const cleanProductName = item.multisistem ? (p1Base + p2Base + ms) : p1Base;

        const row = [
          cleanProductType,
          cleanRoomName,
          cleanProductName
        ];

        if (includeMeasurements) {
          row.push(`${item.width || '-'} x ${item.height || '-'}`);
        }

        const rowTotal = parseFloat(item.rowTotal) || 0;
        const discountedRowTotal = rowTotal * (1 - discountRate / 100);

        row.push(
          '-', // Plise için pile modeli yok
          item.quantity || '1',
          formatPrice(rowTotal),
          discountRate > 0 ? formatPrice(discountedRowTotal) : '-'
        );

        return row;
      });
    }

    // Diğer ürün tipleri için tek satır
    let productName = order.productName || '';
    if (order.productVariant) productName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

    const row = [
      cleanProductType,
      cleanRoomName,
      cleanProductName
    ];

    if (includeMeasurements) {
      row.push(`${order.width || ''} x ${order.height || ''}`);
    }

    row.push(
      cleanPileModel,
      order.quantity || '1',
      formatPrice(order.totalPrice || 0),
      order.discountedPrice && parseFloat(order.discount) > 0 ? formatPrice(order.discountedPrice) : '-'
    );

    // Ray Sistemi için kullanılan metriği olculer sütununda göster (Müşteri PDF)
    if (order.productType === 'raySistemi' && includeMeasurements) {
      row[3] = `${order.width || ''} cm`; // Boy yok
      const usedMeter = order.usedMeter ? `${order.usedMeter} m` : '-';
      const olcu = order.olcuTamamlama ? ` (${order.olcuTamamlama})` : '';
      row[4] = cleanTurkishChars(order.olcuTamamlama || '-');
      row[3] = `${order.width || ''} cm\n${usedMeter}${olcu}`;
    }

    // Rustik için detayları göster
    if (order.productType === 'rustik' && includeMeasurements) {
      row[3] = `${order.width || ''} cm`; // Boy yok
      const usedCm = order.usedCm ? `${order.usedCm} cm` : '-';
      row[3] = `Ширина: ${order.width || ''} cm\nИсп.: ${usedCm}`;

      let ayakDetay = order.ayakModel || '-';
      if (order.ayakSayisi) {
        ayakDetay += `\nКол-во: ${order.ayakSayisi}`;
        if (order.ekAyakSayisi) ayakDetay += ` (+${order.ekAyakSayisi} доп.)`;
      }
      if (order.ayakCap || order.ayakRenkKodu) {
        ayakDetay += `\n(${order.ayakCap || ''} ${order.ayakRenkKodu || ''})`;
      }
      row[4] = ayakDetay;
    }

    return [row];
  });

  return { head, body };
};

const getDealerProductName = (order, item = null) => {
  const datePrefix = order._sessionDateLabel ? `[${order._sessionDateLabel}] ` : '';

  if (order.productType === 'plise' && item) {
    const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
    const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';
    const ms = item.multisistem ? ' (Мультисистема)' : '';
    const cleanProductName = item.multisistem ? (p1Base + p2Base + ms) : p1Base;
    return `${datePrefix}${cleanProductName}`.trim();
  }

  let productName = order.productName || '';
  if (order.productVariant) productName += ` - ${order.productVariant}`;
  const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');
  return `${datePrefix}${cleanProductName}`.trim();
};

const formatDealerOrderSize = (order, item = null) => {
  if (item) {
    if (item.width && item.height) return `${item.width} x ${item.height}`;
    if (item.width) return `${item.width}`;
    if (item.height) return `${item.height}`;
    return '-';
  }

  if (STOCK_METER_PRODUCT_TYPES.includes(order.productType)) {
    return order.width ? `${order.width} m` : '-';
  }

  if (order.productType === 'raySistemi') {
    const usedMeter = order.usedMeter ? `${order.usedMeter} m` : '-';
    return `${order.width || ''} cm\n${usedMeter}`;
  }

  if (order.productType === 'rustik') {
    const usedCm = order.usedCm ? `${order.usedCm} cm` : '-';
    return `Шир.: ${order.width || ''} cm\nИсп.: ${usedCm}`;
  }

  if (order.width && order.height) {
    return `${order.width} x ${order.height}`;
  }

  if (order.width) {
    return `${order.width}`;
  }

  return '-';
};

const getDealerOrderTotalAmount = (order, item = null) => {
  if (item) {
    return parseFloat(item.rowTotal) || 0;
  }
  return parseFloat(order.totalPrice || 0);
};

const getDealerOrderDiscountedAmount = (order, item = null) => {
  const discountRate = parseFloat(order.discount) || 0;
  if (discountRate <= 0) return null;

  if (item) {
    const rowTotal = parseFloat(item.rowTotal) || 0;
    return rowTotal * (1 - discountRate / 100);
  }

  if (order.discountedPrice != null && order.discountedPrice !== '') {
    return parseFloat(order.discountedPrice);
  }

  const total = parseFloat(order.totalPrice || 0);
  return total * (1 - discountRate / 100);
};

// Bayi / toptan PDF tablo verisi (oda ve pile modeli olmadan)
const buildDealerTableData = (orders, { includeMeasurements = true } = {}) => {
  const head = includeMeasurements
    ? [['Тип товара', 'Название', 'Размеры', 'Кол-во', 'Сумма (₸)', 'Со скидкой (₸)']]
    : [['Тип товара', 'Название', 'Кол-во', 'Сумма (₸)', 'Со скидкой (₸)']];

  const body = orders.flatMap((order) => {
    const cleanProductType = getProductTypeLabel(order.productType);

    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      return order.pliseItems.map((item) => {
        const row = [cleanProductType, getDealerProductName(order, item)];
        if (includeMeasurements) row.push(formatDealerOrderSize(order, item));
        const discountedAmount = getDealerOrderDiscountedAmount(order, item);
        row.push(
          item.quantity || '1',
          formatPrice(getDealerOrderTotalAmount(order, item)),
          discountedAmount != null ? formatPrice(discountedAmount) : '-'
        );
        return row;
      });
    }

    const row = [cleanProductType, getDealerProductName(order)];
    if (includeMeasurements) row.push(formatDealerOrderSize(order));
    const discountedAmount = getDealerOrderDiscountedAmount(order);
    row.push(
      order.quantity || '1',
      formatPrice(getDealerOrderTotalAmount(order)),
      discountedAmount != null ? formatPrice(discountedAmount) : '-'
    );
    return [row];
  });

  return { head, body };
};

const getPdfTableConfig = (orders, options = {}) => {
  const tableVariant = options.tableVariant || 'retail';
  const includeMeasurements = options.includeMeasurements !== false;

  if (tableVariant === 'dealer') {
    return {
      ...buildDealerTableData(orders, { includeMeasurements: true }),
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 50 },
        2: { cellWidth: 28 },
        3: { cellWidth: 12, halign: 'center' },
        4: { cellWidth: 26, halign: 'right' },
        5: { cellWidth: 26, halign: 'right' }
      }
    };
  }

  if (tableVariant === 'dealerPreOrder') {
    return {
      ...buildDealerTableData(orders, { includeMeasurements: false }),
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 58 },
        2: { cellWidth: 14, halign: 'center' },
        3: { cellWidth: 30, halign: 'right' },
        4: { cellWidth: 30, halign: 'right' }
      }
    };
  }

  return {
    ...buildCustomerTableData(orders, includeMeasurements),
    columnStyles: includeMeasurements
      ? {
        0: { cellWidth: 22 },
        1: { cellWidth: 18 },
        2: { cellWidth: 25 },
        3: { cellWidth: 20 },
        4: { cellWidth: 25 },
        5: { cellWidth: 12, halign: 'center' },
        6: { cellWidth: 28, halign: 'right' },
        7: { cellWidth: 25, halign: 'right' }
      }
      : {
        0: { cellWidth: 25 },
        1: { cellWidth: 20 },
        2: { cellWidth: 30 },
        3: { cellWidth: 28 },
        4: { cellWidth: 15, halign: 'center' },
        5: { cellWidth: 28, halign: 'right' },
        6: { cellWidth: 25, halign: 'right' }
      }
  };
};

// Ana Müşteri PDF oluşturma
const generateCustomerPDFBase = async (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, options = {}) => {
  const title = options.title || 'Заказ клиента';
  const showPaymentInfo = options.showPaymentInfo || false;
  const showDetailedPricing = options.showDetailedPricing !== false; // Varsayılan true
  const paidAmount = options.paidAmount || 0;
  const remainingAmount = options.remainingAmount || 0;

  if (!customerInfo || orders.length === 0) {
    alert('Информация о клиенте или заказы не найдены!');
    return;
  }

  const headerOptions = options.showRetailQrCodes
    ? { qrCodes: await getRetailPdfQrCodes(), contactPhone: COMPANY_CONTACT.phone }
    : options.showDealerQrCodes
      ? { qrCodes: await getDealerPdfQrCodes(), contactPhone: COMPANY_CONTACT.dealerPhone }
      : {};

  const doc = new jsPDF();
  registerRobotoFont(doc);

  // Header
  drawHeader(doc, title, '', headerOptions);

  // Müşteri bilgileri
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Sipariş tablosu başlığı
  doc.setFontSize(11);
  doc.setFont(PDF_FONT, 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('ДЕТАЛИ ЗАКАЗА', 20, currentY);
  currentY += 8;

  // Tablo verilerini hazırla
  const { head, body, columnStyles } = getPdfTableConfig(orders, options);

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head,
    body,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center'
    },
    styles: {
      font: PDF_FONT,
      fontSize: 9,
      cellPadding: 4,
      halign: 'center'
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles,
    pageBreak: 'auto',
    margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, title + ' (продолжение)', '', headerOptions);
      }
    }
  });

  // Toplam tutar kutusu
  let finalY = doc.lastAutoTable.finalY + 10;

  // Toplam kutusunun tahmini yüksekliğini hesapla
  let estimatedBoxItems = 1; // Genel toplam
  if (showDetailedPricing) {
    estimatedBoxItems += 1; // Ara toplam
    if (discountedTotalAmount !== totalAmount) estimatedBoxItems++;
    if (genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0) estimatedBoxItems += 2;
  }
  if (showPaymentInfo) estimatedBoxItems += 2;
  const estimatedBoxHeight = 20 + (estimatedBoxItems * 10);

  // Sayfa kontrolü - footer için 35px, kutu için tahmini yükseklik
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + estimatedBoxHeight > pageHeight - 40) {
    doc.addPage();
    drawHeader(doc, title, '', headerOptions);
    finalY = 50;
  }

  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, finalY, showPaymentInfo, paidAmount, remainingAmount, showDetailedPricing, customerInfo, orders);

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  openPdfInNewTab(doc);
};

// Müşteri PDF oluşturma
export const generateCustomerPDF = async (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, paidAmount = 0, remainingAmount = 0) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, {
    tableVariant: 'retail',
    includeMeasurements: true,
    title: 'Детали заказа',
    showPaymentInfo: true,
    showDetailedPricing: false,
    showRetailQrCodes: true,
    paidAmount,
    remainingAmount
  });
};

export const generateDealerCustomerPDF = async (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, paidAmount = 0, remainingAmount = 0) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, {
    tableVariant: 'dealer',
    includeMeasurements: true,
    title: 'Детали заказа',
    showPaymentInfo: true,
    showDetailedPricing: false,
    showDealerQrCodes: true,
    paidAmount,
    remainingAmount
  });
};

export const generateDealerCustomerPreOrderPDF = async (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, {
    tableVariant: 'dealerPreOrder',
    includeMeasurements: false,
    title: 'Коммерческое предложение',
    showPaymentInfo: false,
    showDetailedPricing: true,
    showDealerQrCodes: true
  });
};

// Bayi detayı: seçili sipariş listelerindeki tüm siparişleri tek Müşteri PDF'inde birleştirir
export const generateDealerCombinedCustomerPDF = (dealerInfo, sessionsWithOrders, options = {}) => {
  const { dateFrom = '', dateTo = '' } = options;

  if (!dealerInfo || !Array.isArray(sessionsWithOrders) || sessionsWithOrders.length === 0) {
    alert('Информация о дилере или заказы не найдены!');
    return;
  }

  const allOrders = [];
  let totalPaid = 0;
  let totalRemaining = 0;
  let combinedGrandTotal = 0;

  sessionsWithOrders.forEach(({ session, orders }) => {
    if (!Array.isArray(orders) || orders.length === 0) return;

    const sessionDateLabel = formatPdfDateLabel(session.orderDate || session.sessionDate);
    orders.forEach((order) => {
      allOrders.push({
        ...order,
        _sessionDateLabel: sessionDateLabel
      });
    });

    totalPaid += parseFloat(session.paidAmount) || 0;
    const sessionRemaining = session.remainingAmount != null && session.remainingAmount !== ''
      ? parseFloat(session.remainingAmount) || 0
      : Math.max(0, (parseFloat(session.totalOrderAmount) || 0) - (parseFloat(session.paidAmount) || 0));
    totalRemaining += sessionRemaining;
    combinedGrandTotal += calculateCustomerGrandTotal(session, orders);
  });

  if (allOrders.length === 0) {
    alert('Заказы для PDF не найдены!');
    return;
  }

  const totalAmount = allOrders.reduce((sum, order) => sum + parseFloat(order.totalPrice || 0), 0);
  const discountedTotalAmount = allOrders.reduce((sum, order) => {
    if (order.discountedPrice && parseFloat(order.discount) > 0) {
      return sum + parseFloat(order.discountedPrice || 0);
    }
    return sum + parseFloat(order.totalPrice || 0);
  }, 0);

  let periodSuffix = '';
  if (dateFrom && dateTo) {
    periodSuffix = ` (${formatPdfDateLabel(dateFrom)} - ${formatPdfDateLabel(dateTo)})`;
  } else if (dateFrom) {
    periodSuffix = ` (${formatPdfDateLabel(dateFrom)}+)`;
  } else if (dateTo) {
    periodSuffix = ` (-${formatPdfDateLabel(dateTo)})`;
  }

  const customerInfo = {
    name: `${dealerInfo.companyName || dealerInfo.name || ''}${periodSuffix}`.trim(),
    phone1: dealerInfo.phone1,
    address: dealerInfo.address,
    manualTotalOverride: combinedGrandTotal
  };

  generateDealerCustomerPDF(
    customerInfo,
    allOrders,
    totalAmount,
    discountedTotalAmount,
    '',
    totalPaid,
    totalRemaining
  );
};

// Bayi etiket PDF (120x75 mm)
export const generateDealerLabelPDF = async (labelInfo) => {
  if (!labelInfo || !labelInfo.customer) {
    alert('Информация для этикетки не найдена!');
    return;
  }

  const doc = createLabelPdfDoc();
  await drawDealerLabelPage(doc, labelInfo);
  openPdfInNewTab(doc);
};

// Bayi detayı: seçili sipariş listeleri için etiket PDF (her liste ayrı sayfa)
export const generateDealerCombinedLabelPDF = async (dealerInfo, sessions = []) => {
  if (!dealerInfo || !Array.isArray(sessions) || sessions.length === 0) {
    alert('Информация о дилере или списки заказов не найдены!');
    return;
  }

  const doc = createLabelPdfDoc();
  for (let index = 0; index < sessions.length; index += 1) {
    if (index > 0) doc.addPage([LABEL_SIZE_MM.height, LABEL_SIZE_MM.width], 'landscape');
    await drawDealerLabelPage(doc, buildDealerLabelInfo(dealerInfo, sessions[index]));
  }
  openPdfInNewTab(doc);
};

// Müşteri PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPDFBlob = async (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, paidAmount = 0, remainingAmount = 0) => {
  if (!customerInfo || orders.length === 0) return null;
  const headerOptions = { qrCodes: await getRetailPdfQrCodes(), contactPhone: COMPANY_CONTACT.phone };
  const doc = new jsPDF();
  registerRobotoFont(doc);
  drawHeader(doc, 'Детали заказа', '', headerOptions);
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);
  doc.setFontSize(11); doc.setFont(PDF_FONT, 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('ДЕТАЛИ ЗАКАЗА', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, true);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { font: PDF_FONT, fontSize: 9, cellPadding: 4, halign: 'center' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 18 }, 2: { cellWidth: 25 }, 3: { cellWidth: 20 }, 4: { cellWidth: 25 }, 5: { cellWidth: 12, halign: 'center' }, 6: { cellWidth: 28, halign: 'right' }, 7: { cellWidth: 25, halign: 'right' } },
    pageBreak: 'auto', margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Детали заказа (продолжение)', '', headerOptions); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 80 > pageHeight - 40) { doc.addPage(); drawHeader(doc, 'Детали заказа', '', headerOptions); finalY = 50; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, finalY, true, paidAmount, remainingAmount, false, customerInfo, orders);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Müşteri (Sipariş öncesi) PDF oluşturma - en/boy bilgisi olmadan
export const generateCustomerPreOrderPDF = async (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, {
    tableVariant: 'retail',
    includeMeasurements: false,
    title: 'Коммерческое предложение',
    showPaymentInfo: false,
    showDetailedPricing: true,
    showRetailQrCodes: true
  });
};

// Müşteri (Sipariş öncesi) PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPreOrderPDFBlob = async (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde) => {
  if (!customerInfo || orders.length === 0) return null;
  const headerOptions = { qrCodes: await getRetailPdfQrCodes(), contactPhone: COMPANY_CONTACT.phone };
  const doc = new jsPDF();
  registerRobotoFont(doc);
  drawHeader(doc, 'Коммерческое предложение', '', headerOptions);
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);
  doc.setFontSize(11); doc.setFont(PDF_FONT, 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('ДЕТАЛИ ЗАКАЗА', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, false);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { font: PDF_FONT, fontSize: 9, cellPadding: 4, halign: 'center' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 20 }, 2: { cellWidth: 30 }, 3: { cellWidth: 28 }, 4: { cellWidth: 15, halign: 'center' }, 5: { cellWidth: 28, halign: 'right' }, 6: { cellWidth: 25, halign: 'right' } },
    pageBreak: 'auto', margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Коммерческое предложение (продолжение)', '', headerOptions); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 60 > pageHeight - 40) { doc.addPage(); drawHeader(doc, 'Коммерческое предложение', '', headerOptions); finalY = 50; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, finalY, false, 0, 0, true, customerInfo, orders);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Atölye PDF oluşturma
export const generateWorkshopPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Информация о клиенте или заказы не найдены!');
    return;
  }

  const tulKanatOrders = orders.filter(order =>
    order.productType === 'tul' || order.productType === 'kanat'
  );

  if (tulKanatOrders.length === 0) {
    alert('Для PDF мастерской не найдено товаров Тюль или Полотно!');
    return;
  }

  const doc = new jsPDF();
  registerRobotoFont(doc);

  // Header - Müşteri adını büyük başlık olarak yazdır (Talep üzerine)
  drawHeader(doc, 'Заказ для мастерской', customerInfo.name);

  // Siparişler için kutular
  const cleanCustomerName = cleanTurkishChars(customerInfo.name);
  let currentY = 45;
  let currentX = 12; // Daha geniş alan için kenardan başla
  const boxWidth = 90;
  const boxHeight = 63;
  const margin = 6; // Sütunlar arası boşluğu biraz daralt

  tulKanatOrders.forEach((order) => {
    if (currentY + boxHeight > 270) {
      doc.addPage();
      drawHeader(doc, 'Заказ для мастерской', customerInfo.name);
      currentY = 45;
      currentX = 12;
    }

    // Kutu arka planı
    doc.setFillColor(...LIGHT_BG);
    doc.roundedRect(currentX, currentY, boxWidth, boxHeight, 3, 3, 'F');

    // Kutu kenarlığı
    doc.setDrawColor(...PRIMARY_COLOR);
    doc.setLineWidth(0.5);
    doc.roundedRect(currentX, currentY, boxWidth, boxHeight, 3, 3, 'S');

    // Üst şerit - müşteri adı + oda adı
    doc.setFillColor(...PRIMARY_COLOR);
    doc.roundedRect(currentX, currentY, boxWidth, 14, 3, 3, 'F');
    doc.rect(currentX, currentY + 7, boxWidth, 7, 'F'); // Alt köşeleri düzelt

    doc.setFontSize(10);
    doc.setFont(PDF_FONT, 'bold');
    doc.setTextColor(255, 255, 255);

    const cleanRoomName = cleanTurkishChars(order.roomName || '');
    // Sol: Müşteri Adı (Talep üzerine başlık), Sağ: Oda adı
    doc.text(cleanCustomerName.substring(0, 25), currentX + 4, currentY + 9);
    doc.setFontSize(8);
    doc.text(cleanRoomName, currentX + boxWidth - 4, currentY + 9, { align: 'right' });

    // İçerik
    doc.setFont(PDF_FONT, 'normal');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);

    let fullProductName = order.productName || '';
    if (order.productVariant) fullProductName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(fullProductName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');
    const cleanPileModel = getPdfPileModel(order.pileModel);
    const cleanNotes = cleanTurkishChars(order.notes || '');

    doc.text(`Товар: ${cleanProductName.substring(0, 28)}`, currentX + 4, currentY + 20);
    doc.text(`Складка: ${cleanPileModel.substring(0, 25)}`, currentX + 4, currentY + 28);
    doc.setFont(PDF_FONT, 'bold');
    doc.text(`Ширина: ${order.width || '-'} см  Высота: ${order.height || '-'} см  Кол-во: ${order.quantity || '1'}`, currentX + 4, currentY + 36);

    // Not alanı
    if (cleanNotes) {
      doc.setFontSize(10);
      doc.setFont(PDF_FONT, 'normal');
      doc.setTextColor(...GRAY_TEXT);
      const noteLines = splitTextToLines(cleanNotes, 42);
      let noteY = currentY + 44;
      doc.text('Прим.:', currentX + 4, noteY);
      noteLines.slice(0, 2).forEach((line, i) => {
        doc.text(line, currentX + 4, noteY + 6 + (i * 5));
      });
    }

    doc.setTextColor(0, 0, 0);

    // Sonraki kutu pozisyonu
    currentX += boxWidth + margin;

    if (currentX + boxWidth > 200) {
      currentX = 12;
      currentY += boxHeight + 10; // Satır arası boşluk
    }
  });

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  openPdfInNewTab(doc);
};

// Montaj PDF oluşturma
export const generateInstallationPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Информация о клиенте или заказы не найдены!');
    return;
  }

  const doc = new jsPDF();
  registerRobotoFont(doc);

  // Header
  drawHeader(doc, 'Заказ на монтаж');

  // Müşteri bilgileri kutusu
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Sipariş listesi başlığı
  doc.setFontSize(11);
  doc.setFont(PDF_FONT, 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('СПИСОК МОНТАЖА', 20, currentY);
  currentY += 8;

  // Siparişleri oda gruplarına göre sırala
  const sortedOrders = [...orders].sort((a, b) => {
    const roomGroupA = (a.roomName || '').substring(0, 3).toUpperCase();
    const roomGroupB = (b.roomName || '').substring(0, 3).toUpperCase();

    if (roomGroupA !== roomGroupB) {
      return roomGroupA.localeCompare(roomGroupB, 'tr-TR');
    }

    const roomCompare = (a.roomName || '').localeCompare(b.roomName || '', 'tr-TR');
    if (roomCompare !== 0) return roomCompare;

    return (a.productType || '').localeCompare(b.productType || '', 'tr-TR');
  });

  // Tablo verileri
  const tableData = sortedOrders.flatMap((order) => {
    const cleanProductType = getProductTypeLabel(order.productType);
    const cleanRoomName = order.roomName || '';

    // Plise için detaylı satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      return order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Мультисистема)' : '';
        const namePart = item.multisistem ? (p1Base + p2Base + ms) : p1Base;
        const dimensionPart = ` [${item.width} x ${item.height}] - ${item.quantity} шт.`;
        const fullProductName = namePart + dimensionPart;

        return [
          cleanRoomName,
          cleanProductType,
          fullProductName
        ];
      });
    }

    // Diğer ürünler için
    let productName = order.productName || '';
    if (order.productVariant) productName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

    return [[
      cleanRoomName,
      cleanProductType,
      cleanProductName
    ]];
  });

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head: [['Комната', 'Тип товара', 'Название']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 10
    },
    styles: {
      font: PDF_FONT,
      fontSize: 10,
      cellPadding: 5
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 45 },
      2: { cellWidth: 75 }
    },
    margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, 'Заказ на монтаж (продолжение)');
      }
    }
  });

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  openPdfInNewTab(doc);
};
